import { GanttTask } from '../types/gantt';
import { parseTableText } from '../utils/pasteParser';

declare const Office: any;
declare const PowerPoint: any;
declare const Word: any;
declare const Excel: any;
declare const OneNote: any;

export type OfficeHostType = 'PowerPoint' | 'Excel' | 'Word' | 'OneNote' | 'Browser';

export interface OfficeInsertResult {
  success: boolean;
  message: string;
  host: OfficeHostType;
}

/**
 * Detects the current active Office host application.
 */
export function getActiveHost(): OfficeHostType {
  try {
    if (typeof Office !== 'undefined' && Office.context?.host) {
      switch (Office.context.host) {
        case Office.HostType.PowerPoint:
          return 'PowerPoint';
        case Office.HostType.Excel:
          return 'Excel';
        case Office.HostType.Word:
          return 'Word';
        case Office.HostType.OneNote:
          return 'OneNote';
        default:
          return 'PowerPoint';
      }
    }
  } catch {
    // Not running in Office
  }
  return 'Browser';
}

/**
 * Checks if the add-in is running inside an active Office host.
 */
export function isRunningInOffice(): boolean {
  return getActiveHost() !== 'Browser';
}

/**
 * Inserts the generated Gantt chart into the active Office document/slide/sheet/page.
 */
export async function insertGanttIntoHost(
  svgString: string,
  _tasks?: GanttTask[]
): Promise<OfficeInsertResult> {
  const host = getActiveHost();

  if (host === 'Browser') {
    // Standalone browser mode fallback: Copy PNG Image to clipboard
    const copied = await copyChartImageToClipboard(svgString);
    return {
      success: copied,
      message: copied
        ? 'Running in Browser Preview. Chart image copied! Paste directly into your slide/document with Cmd+V / Ctrl+V.'
        : 'Running in Browser Preview. Could not copy image.',
      host: 'Browser',
    };
  }

  // Running inside Office!
  try {
    // 1. First priority: Native Vector SVG insertion (enables 'Convert to Shape' in PowerPoint)
    if (typeof Office !== 'undefined' && Office.context?.document?.setSelectedDataAsync) {
      const svgInserted = await new Promise<boolean>((resolve) => {
        try {
          Office.context.document.setSelectedDataAsync(
            svgString,
            {
              coercionType: Office.CoercionType.XmlSvg,
              imageLeft: 50,
              imageTop: 80,
              imageWidth: 620,
            },
            (result: any) => {
              resolve(result.status === Office.AsyncResultStatus.Succeeded);
            }
          );
        } catch {
          resolve(false);
        }
      });

      if (svgInserted) {
        return {
          success: true,
          message: `Gantt chart inserted as vector SVG! Right-click it in PowerPoint and choose "Convert to Shape" to edit shapes.`,
          host,
        };
      }
    }

    // 2. Fallback: PNG raster insertion for older Office versions
    const pngBase64 = await convertSvgToPngBase64(svgString, 2);
    const rawBase64 = pngBase64.replace(/^data:image\/png;base64,/, '');

    // Host-specific specialized insertions
    if (host === 'Word') {
      try {
        return await tryWordInsertion(rawBase64);
      } catch {
        // Fallback to setSelectedDataAsync
      }
    } else if (host === 'Excel') {
      try {
        return await tryExcelInsertion(rawBase64);
      } catch {
        // Fallback
      }
    } else if (host === 'OneNote') {
      try {
        return await tryOneNoteInsertion(rawBase64);
      } catch {
        // Fallback
      }
    } else if (host === 'PowerPoint') {
      try {
        return await tryPowerPointRunInsertion(rawBase64);
      } catch {
        // Fallback
      }
    }

    // Universal Office.context.document fallback
    return new Promise((resolve) => {
      Office.context.document.setSelectedDataAsync(
        rawBase64,
        {
          coercionType: Office.CoercionType.Image,
          imageLeft: 50,
          imageTop: 80,
          imageWidth: 620,
        },
        (result: any) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve({
              success: true,
              message: `Gantt chart successfully inserted into ${host}!`,
              host,
            });
          } else {
            resolve({
              success: false,
              message: `Insertion error: ${result.error?.message || 'Unknown error'}`,
              host,
            });
          }
        }
      );
    });
  } catch (error: any) {
    console.error('Error inserting into host:', error);
    return {
      success: false,
      message: `Failed to insert into ${host}: ${error?.message || error}`,
      host,
    };
  }
}

async function tryPowerPointRunInsertion(base64Image: string): Promise<OfficeInsertResult> {
  if (typeof PowerPoint !== 'undefined' && PowerPoint.run) {
    return PowerPoint.run(async (context: any) => {
      const selectedSlide = context.presentation.getSelectedSlides().getItemAt(0);
      selectedSlide.shapes.addImage(base64Image);
      await context.sync();
      return {
        success: true,
        message: 'Gantt chart inserted into slide!',
        host: 'PowerPoint',
      };
    });
  }
  throw new Error('PowerPoint.run is not supported.');
}

async function tryWordInsertion(base64Image: string): Promise<OfficeInsertResult> {
  if (typeof Word !== 'undefined' && Word.run) {
    return Word.run(async (context: any) => {
      const selection = context.document.getSelection();
      selection.insertInlinePictureFromBase64(base64Image, 'Replace');
      await context.sync();
      return {
        success: true,
        message: 'Gantt chart inserted into Word document at cursor position!',
        host: 'Word',
      };
    });
  }
  throw new Error('Word.run is not supported.');
}

async function tryExcelInsertion(base64Image: string): Promise<OfficeInsertResult> {
  if (typeof Excel !== 'undefined' && Excel.run) {
    return Excel.run(async (context: any) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.shapes.addImage(base64Image);
      await context.sync();
      return {
        success: true,
        message: 'Gantt chart inserted onto active Excel sheet!',
        host: 'Excel',
      };
    });
  }
  throw new Error('Excel.run is not supported.');
}

async function tryOneNoteInsertion(base64Image: string): Promise<OfficeInsertResult> {
  if (typeof OneNote !== 'undefined' && OneNote.run) {
    return OneNote.run(async (context: any) => {
      const page = context.application.getActivePage();
      page.addImage(base64Image);
      await context.sync();
      return {
        success: true,
        message: 'Gantt chart inserted onto OneNote page!',
        host: 'OneNote',
      };
    });
  }
  throw new Error('OneNote.run is not supported.');
}

/**
 * Excel Superpower: Reads selected spreadsheet range and converts to Gantt tasks.
 */
export async function readExcelSelectedRange(): Promise<{ tasks: GanttTask[]; error?: string }> {
  if (typeof Excel !== 'undefined' && Excel.run) {
    try {
      return await Excel.run(async (context: any) => {
        const range = context.workbook.getSelectedRange();
        range.load(['values', 'rowCount', 'columnCount']);
        await context.sync();

        const values: any[][] = range.values;
        if (!values || values.length === 0) {
          return { tasks: [], error: 'No cells selected in Excel sheet.' };
        }

        const tsvText = values.map((row) => row.join('\t')).join('\n');
        return parseTableText(tsvText);
      });
    } catch (e: any) {
      return { tasks: [], error: `Failed to read Excel range: ${e?.message || e}` };
    }
  }
  return { tasks: [], error: 'Excel API is not active.' };
}

/**
 * Copies the chart as a visual PNG image to the system clipboard.
 */
export async function copyChartImageToClipboard(svgString: string, scale = 2): Promise<boolean> {
  try {
    const pngBlob = await convertSvgToPngBlob(svgString, scale);
    if (navigator.clipboard && navigator.clipboard.write && typeof ClipboardItem !== 'undefined') {
      const item = new ClipboardItem({
        'image/png': pngBlob,
      });
      await navigator.clipboard.write([item]);
      return true;
    }
    return false;
  } catch (e) {
    console.error('Clipboard copy image error:', e);
    return false;
  }
}

/**
 * Downloads SVG string as a file.
 */
export function downloadSvg(svgString: string, filename = 'gantt-chart.svg') {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Converts SVG string into PNG Blob using HTML5 Canvas.
 */
export function convertSvgToPngBlob(svgString: string, scale = 2): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas context not available'));
        return;
      }
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob returned null'));
        }
      }, 'image/png');
    };

    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    img.src = url;
  });
}

/**
 * Converts SVG string into high-resolution PNG base64 string using HTML5 Canvas.
 */
export function convertSvgToPngBase64(svgString: string, scale = 2): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas context not available'));
        return;
      }
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    img.src = url;
  });
}
